/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U3134InitLU, U3134WorkInProgressSearchRow } from "./U3134Service";
import { U2000_ORG_DISPLAY_TIMEZONE, u2000SetActiveDisplayTimezone } from "../u2000Core/tools/U2000DateTimeHelpers";

export type U3134InitLUState = U2000LUState["personalization"] &
  U3134InitLU & {
    launchSearch: boolean;
    hideSearch: boolean;
    nbRecordPerPage: number;
  };

export interface U3134ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3134WorkInProgressSearchRow>;
  initLU: U3134InitLU;
}

interface U3134ContextProviderProps {
  initLU: U3134InitLU;
  children: ReactNode;
}

const U3134Context = React.createContext<U3134ContextType | undefined>(undefined);

export const U3134ContextProvider: React.FC<U3134ContextProviderProps> = ({ initLU, children }) => {
  u2000SetActiveDisplayTimezone(initLU.organizationTimezone ?? U2000_ORG_DISPLAY_TIMEZONE);

  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as U3134InitLUState & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({
    luName: "u3134",
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3134WorkInProgressSearchRow>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 50,
  });

  const contextValue = useMemo<U3134ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      initLU,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers, initLU],
  );

  return <U3134Context.Provider value={contextValue}>{children}</U3134Context.Provider>;
};

export function useU3134Context(): U3134ContextType {
  const context = useContext(U3134Context);
  if (!context) {
    throw new Error("useU3134Context must be used within a U3134ContextProvider");
  }
  return context;
}
